import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@/types/query';
import * as OrderService from '@/services/order';
import { toRQMutation, toRQQuery } from '@/utilities/service';

export function useOrder(id: number, enabled?: boolean) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.ORDER, id],
    queryFn: () => OrderService.getOrder(id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    enabled: enabled != null ? enabled : id > 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useOrderRefundDetail(id: number) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.ORDER_REFUND_DETAIL, id],
    queryFn: () => OrderService.getOrderRefundDetail(id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useOrderRefund() {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: OrderService.CreateRefundData) => OrderService.createOrderRefund(data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useOrderExtraRefundDetail(id: number) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.ORDER_EXTRA_REFUND_DETAIL, id],
    queryFn: () => OrderService.getOrderExtraRefundDetail(id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useOrderExtraRefund(id: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: OrderService.CreateExtraRefundData) => OrderService.createOrderExtraRefund(id, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useOrderInvoice(orderId: number, buyerID: number, isLitigation: boolean) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ORDER_INVOICE, orderId, buyerID, isLitigation],
    queryFn: () => OrderService.getOrderInvoice(orderId, buyerID, isLitigation),
    cacheTime: 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useRemovePickupDeliveryAddress(orderId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => OrderService.removePickupDeliveryAddress(orderId),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useGetChargeBack(orderId: string) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.GET_CHARGE_BACK, orderId],
    queryFn: () => OrderService.getChargeBack(orderId),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}
